<script setup>
import { computed, onMounted, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useCustomerStore } from "@/stores/CustomersStore"
import { useCalendarStore } from "@/views/apps/calendar/useCalendarStore"
import { useEventStore } from "@/stores/EventsStore"
import { $backend } from "@/utils/backend"
import { normalize } from "@/utils/strings"
import { extractDate, extractTime } from "@/utils/datetime"
import { useDisplay } from "vuetify"
import emitter from "@/utils/emitter.js"

const props = defineProps({
  full: Boolean, // customer card: search, status filter, sorting and both dates
})

const { t } = useI18n()
const customerStore = useCustomerStore()
const calendarStore = useCalendarStore()
const { xs, mobile, smAndDown } = useDisplay()

const isLoading = ref(false)
const hasError = ref(false)
const cardUrl = ref(null)
const treatments = ref([])

const statusProps = {
  new: { color: 'warning', icon: 'ri-time-line' },
  finished: { color: 'info', icon: 'ri-check-line' },
  authorized: { color: 'green', icon: 'ri-shield-check-line' },
}

// ========== DATA ==========

const fetchTreatments = async () => {
  const salonerClientId = customerStore.selectedCustomer?.salonerClientId
  if (!salonerClientId) return

  isLoading.value = true
  hasError.value = false
  try {
    const response = await $backend('/integrations/beautydoc/customer', {
      method: 'POST',
      body: { salonerClientId },
    })

    hasError.value = !response?.success
    cardUrl.value = response?.data?.url || null
    treatments.value = response?.data?.treatments || []
  } catch (e) {
    hasError.value = true
  } finally {
    isLoading.value = false
  }
}

onMounted(fetchTreatments)

// ========== FILTERING / SORTING ==========

const STATUS_ALL = 'all'

const search = ref('')
const statusFilter = ref(STATUS_ALL)
const selectedSortingOption = ref('createdAt-desc')
const visibleCount = ref(5) // visit tab only — the endpoint has no pagination, we just reveal more

const canClearFilters = computed(() => statusFilter.value !== STATUS_ALL || Boolean(search.value))

const clearFilters = () => {
  search.value = ''
  statusFilter.value = STATUS_ALL
}

const sortingOptions = computed(() => ['createdAt-desc', 'createdAt-asc', 'updatedAt-desc', 'updatedAt-asc'].map(value => ({
  value,
  title: t(`beautyDoc.sorting.${value.replace('-', '_')}`),
  icon: value.endsWith('-desc') ? 'ri-sort-desc' : 'ri-sort-asc',
})))

// Search and status filter are rendered only in the customer card, so they are no-ops in the visit tab
const filteredTreatments = computed(() => {
  const query = normalize(search.value)
  const [key, order] = selectedSortingOption.value.split('-')

  return treatments.value
    .filter(item => (statusFilter.value === STATUS_ALL || item.status === statusFilter.value)
      && (!query || normalize(item.name).includes(query)))
    .sort((a, b) => (order === 'desc' ? -1 : 1) * (new Date(a[key]) - new Date(b[key])))
})

const visibleTreatments = computed(() => (props.full
  ? filteredTreatments.value
  : filteredTreatments.value.slice(0, visibleCount.value)))

// ========== HELPERS ==========

const formatDateTime = value => {
  if (!value) return ''

  const date = new Date(value)

  return `${extractDate(date)} ${extractTime(date)}`
}

// A form with status "new" has no signature yet, so the visit tab shows its creation date
const mainDate = item => (item.status === 'new' ? item.createdAt : item.updatedAt)

const openInBeautyDoc = url => {
  if(calendarStore.integrations?.beautyDoc?.showBlockade) {
    setTimeout(() => {
      emitter.emit('show-lock', {
        show: true,
        eventId: null,
        showTransactionSuccessToggle: false,
      })
    }, 500)
  }
  if (url) window.open(url, '_blank', 'noopener')
  fetchTreatments()
}
</script>

<template>
  <div class="mt-1">
    <VSkeletonLoader
      v-if="isLoading"
      :type="props.full ? 'text, list-item-avatar-two-line@8' : 'list-item-avatar-two-line@5'"
      class="bg-transparent"
    />

    <VAlert
      v-else-if="hasError"
      variant="tonal"
      color="error"
      density="compact"
      icon="ri-error-warning-line"
    >
      {{ $t('beautyDoc.connection_error') }}
    </VAlert>

    <template v-else>
      <!-- Visit tab: form counter on the left, actions on the right -->
      <div
        v-if="!props.full"
        class="d-flex justify-space-between align-center ga-2"
      >
        <div class="text-body-2">
          {{ $t('beautyDoc.forms_count') }}
          <span class="ml-1 text-high-emphasis">{{ treatments.length }}</span>
        </div>
        <div class="d-flex ga-2">
          <VBtn
            variant="flat"
            size="small"
            color="secondary"
            prepend-icon="ri-add-large-line"
            :disabled="!cardUrl"
            @click="openInBeautyDoc(cardUrl)"
          >
            {{ $t('beautyDoc.create_treatment') }}
          </VBtn>
          <VBtn
            variant="flat"
            size="small"
            color="secondary"
            prepend-icon="ri-refresh-line"
            @click="fetchTreatments"
          >
            <template #prepend>
              <VIcon
                :class="[{'mr-n2' : !mobile}]"
                size="18"
              />
              <VTooltip
                v-if="!mobile"
                activator="parent"
                location="top"
              >
                {{ $t('refresh') }}
              </VTooltip>
            </template>
          </VBtn>
        </div>
      </div>

      <!-- Customer card: same toolbar layout as the vouchers tab -->
      <div
        v-else
        class="d-flex flex-wrap ga-2 justify-end align-center mb-4"
      >
        <div
          class="d-flex flex-wrap ga-1 mr-auto"
          :class="smAndDown ? 'w-100' : ''"
        >
          <VChipGroup
            :model-value="statusFilter"
            class="chip-group-custom w-100"
            selected-class="v-chip--selected"
            variant="outlined"
            mandatory
            center-active
            scroll-to-active
            filter
            :show-arrows="mobile"
            @update:model-value="statusFilter = $event ?? STATUS_ALL"
          >
            <VChip :value="STATUS_ALL">
              {{ $t('filter_chips.all') }}
            </VChip>
            <VChip
              v-for="status in Object.keys(statusProps)"
              :key="status"
              :value="status"
            >
              {{ $t(`beautyDoc.status.${status}`) }}
            </VChip>
          </VChipGroup>
        </div>
        <VTextField
          v-model="search"
          density="compact"
          color="secondary"
          hide-details
          autocomplete="off"
          clearable
          prepend-inner-icon="ri-search-line"
          :class="mobile ? 'w-100' : ''"
          :style="mobile ? '' : 'min-width:200px; max-width:360px;'"
          :label="$t('search')"
        />
        <!-- Actions stay one flex item, so the dividers never get left behind on wrap -->
        <div
          class="d-flex align-center ga-2"
          :class="mobile ? 'mt-2' : ''"
          :style="smAndDown ? 'flex: 1 1 auto;' : ''"
        >
          <VBtn
            variant="outlined"
            :title="$t('clear_filters')"
            :class="smAndDown ? 'flex-grow-1' : ''"
            :disabled="!canClearFilters"
            :color="canClearFilters ? 'primary' : 'gray'"
            @click="clearFilters"
          >
            <template #prepend>
              <VIcon
                icon="ri-filter-off-line"
                :class="smAndDown ? '' : 'mr-n2'"
              />
            </template>
            <span v-if="smAndDown">{{ $t('clear_filters') }}</span>
          </VBtn>
          <VDivider
            v-if="!mobile"
            vertical
            class="mx-1"
          />
          <VBtn
            variant="outlined"
            color="secondary"
            :title="$t('refresh')"
            @click="fetchTreatments"
          >
            <VIcon icon="ri-reset-left-line" />
          </VBtn>
          <VBtn
            variant="outlined"
            color="secondary"
            :title="$t('beautyDoc.create_treatment')"
            :disabled="!cardUrl"
            @click="openInBeautyDoc(cardUrl)"
          >
            <VIcon icon="ri-add-large-line" />
            <VTooltip
              v-if="!mobile"
              activator="parent"
              location="top"
            >
              {{ $t('beautyDoc.create_treatment') }}
            </VTooltip>
          </VBtn>
          <VDivider
            v-if="!mobile"
            vertical
            class="mx-1"
          />
        </div>
        <VSelect
          v-model="selectedSortingOption"
          :items="sortingOptions"
          :label="$t('sorting')"
          density="compact"
          color="secondary"
          hide-details
          :class="mobile ? 'mt-2' : ''"
          :style="mobile ? 'inline-size: 200px;' : 'max-width:200px;'"
        >
          <template #item="{ props: itemProps, item }">
            <VListItem v-bind="itemProps">
              <template #prepend>
                <VIcon :icon="item.raw.icon" />
              </template>
            </VListItem>
          </template>
        </VSelect>
      </div>

      <VList
        density="compact"
        class="pb-0 bg-transparent"
        :class="props.full ? 'pt-0' : 'pt-2'"
      >
        <VListItem
          v-for="item in visibleTreatments"
          :key="item.id"
          class="px-0"
        >
          <template
            v-if="!xs"
            #prepend
          >
            <VIcon
              :icon="statusProps[item.status]?.icon"
              :color="statusProps[item.status]?.color"
              class="me-1"
            />
          </template>
          <VListItemTitle class="text-wrap lh-1 mr-2">
            {{ item.name }}
          </VListItemTitle>
          <VListItemSubtitle v-if="props.full">
            <span class="mr-4 text-no-wrap">{{ $t('beautyDoc.date_label.new') }}: {{ formatDateTime(item.createdAt) }}</span>
            <span v-if="!props.full || item.status !== 'new'">{{ $t(`beautyDoc.date_label.${item.status}`) }}: {{ formatDateTime(props.full ? item.updatedAt : mainDate(item)) }}</span>
          </VListItemSubtitle>
          <VListItemSubtitle v-else>
            <span v-if="!props.full || item.status !== 'new'">{{ $t(`beautyDoc.date_label.${item.status}`) }}: {{ formatDateTime(props.full ? item.updatedAt : mainDate(item)) }}</span>
            <span
              v-else
              class="mr-4 text-no-wrap"
            >{{ $t('beautyDoc.date_label.new') }}: {{ formatDateTime(item.createdAt) }}</span>
          </VListItemSubtitle>
          <template #append>
            <div class="d-flex align-center ga-2">
              <VChip
                size="small"
                variant="tonal"
                :color="statusProps[item.status]?.color"
              >
                {{ $t(`beautyDoc.status.${item.status}`) }}
              </VChip>
              <VBtn
                size="small"
                :color="props.full ? 'secondary' : 'gray'"
                :variant="props.full ? 'flat' : 'text'"
                :icon="!props.full"
                :disabled="!item.url"
                @click="openInBeautyDoc(item.url)"
              >
                <VIcon icon="ri-link" />
                <VTooltip
                  v-if="!mobile"
                  activator="parent"
                  location="top"
                >
                  {{ $t('beautyDoc.open_treatment') }}
                </VTooltip>
              </VBtn>
            </div>
          </template>
        </VListItem>
        <VListItem
          v-if="!visibleTreatments.length"
          class="px-0 text-disabled"
        >
          {{ $t('no_results') }}
        </VListItem>
      </VList>

      <VBtn
        v-if="!props.full && filteredTreatments.length > visibleCount"
        variant="text"
        color="secondary"
        size="small"
        block
        append-icon="ri-arrow-down-s-line"
        @click="visibleCount += 5"
      >
        {{ $t('see_more') }}
      </VBtn>
    </template>
  </div>
</template>
